import { useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/lib/auth-context";
import { AuthShell, GoogleMark, AppleMark } from "@/components/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MailCheck, RotateCcw } from "lucide-react";
import {
  consumeOAuthErrorFromUrl,
  describeOAuthError,
  type OAuthFailure,
} from "@/lib/oauth-errors";

export type AuthMode = "signin" | "signup" | "forgot" | "check-email";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Only in-app paths may be used as a post-auth destination. */
export function safeRedirect(value: string | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  if (/^\/(signin|signup|auth|forgot-password|verify-email)(\/|$)/.test(value)) return "/";
  return value;
}

/**
 * The whole account surface (sign in, join, reset, verify) in one place so the
 * public routes /signin, /signup, /forgot-password and /verify-email can each
 * open the right step while sharing one implementation.
 */
export function AuthFlow({ initialMode, redirectTo }: { initialMode: AuthMode; redirectTo?: string }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const dest = safeRedirect(redirectTo);

  useEffect(() => {
    if (!loading && user && mode !== "check-email") navigate({ to: dest });
  }, [user, loading, mode, navigate, dest]);

  if (mode === "check-email") return <CheckEmail onBack={() => setMode("signin")} />;
  if (mode === "forgot") return <ForgotPassword onBack={() => setMode("signin")} />;
  if (mode === "signup") return <SignUp dest={dest} onDone={() => setMode("check-email")} onSignIn={() => setMode("signin")} onForgot={() => setMode("forgot")} />;
  return <SignIn dest={dest} onForgot={() => setMode("forgot")} onSignUp={() => setMode("signup")} />;
}

/**
 * Google + Apple share one pending state so a click disables both buttons, keeps
 * the pressed provider's spinner in place through the redirect, and renders at
 * identical sizes on phone, tablet and desktop.
 */
function OAuthButtons({ dest = "/" }: { dest?: string }) {
  const [pending, setPending] = useState<"google" | "apple" | null>(null);
  const [failure, setFailure] = useState<OAuthFailure | null>(null);
  const [lastProvider, setLastProvider] = useState<"google" | "apple" | null>(null);

  // A provider that bounces back with ?error=access_denied (Apple's wording for
  // "I tapped Cancel") lands here, not in the click handler.
  useEffect(() => {
    const fromUrl = consumeOAuthErrorFromUrl();
    if (fromUrl) setFailure(fromUrl);
  }, []);

  const start = async (provider: "google" | "apple", label: string) => {
    if (pending) return;
    setPending(provider);
    setLastProvider(provider);
    setFailure(null);
    // Come back to the public /signin route carrying the intended destination so
    // the session can hydrate before we navigate into a protected page.
    const target = safeRedirect(dest);
    const redirect_uri =
      target === "/"
        ? window.location.origin
        : `${window.location.origin}/signin?redirect=${encodeURIComponent(target)}`;
    const result = await lovable.auth.signInWithOAuth(provider, { redirect_uri });
    if (result.error) {
      setFailure(describeOAuthError(provider, result.error.message));
      setPending(null);
      return;
    }
    // On success the browser is leaving this page (redirect) or the session is
    // set; either way keep the button locked so it can't be pressed twice.
    void label;
  };

  const providers: Array<{ id: "google" | "apple"; label: string }> = [
    { id: "google", label: "Google" },
    { id: "apple", label: "Apple" },
  ];

  return (
    <div className="grid gap-2.5">
      {failure && (
        <div
          role="status"
          aria-live="polite"
          className={
            failure.kind === "cancelled"
              ? "rounded-xl border border-border bg-muted/50 px-3.5 py-3 text-[13px]"
              : "rounded-xl border border-destructive/35 bg-destructive/10 px-3.5 py-3 text-[13px]"
          }
        >
          <p className="font-semibold">{failure.title}</p>
          <p className="mt-0.5 text-muted-foreground">{failure.message}</p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {failure.retryable && lastProvider && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8"
                onClick={() => start(lastProvider, lastProvider === "apple" ? "Apple" : "Google")}
                disabled={pending !== null}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Try again
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setFailure(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {providers.map(({ id, label }) => (
        <Button
          key={id}
          type="button"
          variant="outline"
          className="h-11 w-full font-medium disabled:opacity-60"
          onClick={() => start(id, label)}
          disabled={pending !== null}
          aria-busy={pending === id}
          aria-label={`Continue with ${label}`}
        >
          {pending === id ? (
            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
          ) : id === "apple" ? (
            <AppleMark />
          ) : (
            <GoogleMark />
          )}
          Continue with {label}
        </Button>
      ))}
    </div>
  );
}



function Divider() {
  return (
    <div className="my-6 flex items-center gap-4">
      <span className="h-px flex-1 bg-border" />
      <span className="text-xs uppercase tracking-wide text-muted-foreground">or</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function FieldError({ children }: { children?: string }) {
  if (!children) return null;
  return <p className="text-[13px] text-destructive">{children}</p>;
}

/* ------------------------------ Sign in ------------------------------ */

function SignIn({ dest, onForgot, onSignUp }: { dest: string; onForgot: () => void; onSignUp: () => void }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!password) next.password = "Password is required.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) {
      const msg = /email not confirmed/i.test(error.message)
        ? "Please verify your email address before signing in."
        : "Incorrect email or password.";
      setErrors({ form: msg });
      return;
    }
    navigate({ to: dest });
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to pick up where you left off in your community."
      footer={
        <>
          New to Xamseen?{" "}
          <button type="button" onClick={onSignUp} className="font-medium text-primary hover:underline">
            Create account
          </button>
        </>
      }
    >
      <OAuthButtons dest={dest} />

      <Divider />
      <form onSubmit={submit} noValidate className="grid gap-4">
        {errors.form && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-[13px] text-destructive">
            {errors.form}
          </div>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="in-email">Email</Label>
          <Input id="in-email" type="email" className="h-11" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <FieldError>{errors.email}</FieldError>
        </div>
        <div className="grid gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="in-password">Password</Label>
            <button type="button" onClick={onForgot} className="text-[13px] text-primary hover:underline">
              Forgot password?
            </button>
          </div>
          <Input id="in-password" type="password" className="h-11" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          <FieldError>{errors.password}</FieldError>
        </div>
        <Button type="submit" disabled={busy} className="mt-1 h-11 w-full font-medium">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
        </Button>
      </form>
    </AuthShell>
  );
}

/* ------------------------------ Sign up ------------------------------ */

function SignUp({ dest = "/", onDone, onSignIn, onForgot }: { dest?: string; onDone: () => void; onSignIn: () => void; onForgot: () => void }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<{ provider: string | null } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!first.trim()) next["first"] = "First name is required.";
    if (!email.trim()) next["email"] = "Email is required.";
    else if (!EMAIL_RE.test(email.trim())) next["email"] = "Please enter a valid email address.";
    if (password.length < 8) next["password"] = "Use at least 8 characters.";
    if (confirm !== password) next["confirm"] = "Passwords do not match.";
    if (!agreed) next["agreed"] = "Please accept the Community Guidelines to continue.";
    setErrors(next);
    setExisting(null);
    if (Object.keys(next).length) return;

    setBusy(true);

    // One email, one account: if it already exists, send the person to sign in
    // (or password reset) instead of creating a second, silent signup.
    try {
      const { lookupAccount } = await import("@/lib/account-check.functions");
      const found = await lookupAccount({ data: { email: email.trim() } });
      if (found.exists) {
        setBusy(false);
        setExisting({ provider: found.provider });
        return;
      }
    } catch {
      // Lookup is a convenience; fall through to the normal signup path.
    }

    const fullName = [first.trim(), last.trim()].filter(Boolean).join(" ");
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
        data: { first_name: first.trim(), last_name: last.trim() || null, full_name: fullName },
      },
    });
    setBusy(false);

    if (error) {
      if (/already registered|already been registered/i.test(error.message)) {
        setExisting({ provider: "email" });
      } else {
        setErrors({ form: error.message });
      }
      return;
    }
    onDone();
  };


  return (
    <AuthShell
      title="Join Xamseen"
      subtitle="Create your account and start participating in your community."
      footer={
        <>
          Already have an account?{" "}
          <button type="button" onClick={onSignIn} className="font-medium text-primary hover:underline">
            Sign in
          </button>
        </>
      }
    >
      <OAuthButtons dest={dest} />

      <Divider />
      <form onSubmit={submit} noValidate className="grid gap-4">
        {existing && (
          <div className="grid gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3.5">
            <div>
              <p className="text-[13px] font-semibold">This email already has a Xamseen account</p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {existing.provider && existing.provider !== "email"
                  ? `You signed up with ${existing.provider.charAt(0).toUpperCase() + existing.provider.slice(1)}. Continue with that above to get back in.`
                  : "Sign in with your password, or reset it if you've forgotten it."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={onSignIn}>Sign in</Button>
              <Button type="button" size="sm" variant="outline" onClick={onForgot}>Reset password</Button>
            </div>
          </div>
        )}
        {errors["form"] && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-[13px] text-destructive">
            {errors["form"]}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="su-first">First name</Label>
            <Input id="su-first" className="h-11" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={40} autoComplete="given-name" />
            <FieldError>{errors["first"]}</FieldError>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="su-last" className="text-muted-foreground">Last name <span className="font-normal">(optional)</span></Label>
            <Input id="su-last" className="h-11" value={last} onChange={(e) => setLast(e.target.value)} maxLength={40} autoComplete="family-name" />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="su-email">Email</Label>
          <Input id="su-email" type="email" className="h-11" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <FieldError>{errors["email"]}</FieldError>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="su-password">Password</Label>
          <Input id="su-password" type="password" className="h-11" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          <FieldError>{errors["password"]}</FieldError>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="su-confirm">Confirm password</Label>
          <Input id="su-confirm" type="password" className="h-11" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          <FieldError>{errors["confirm"]}</FieldError>
        </div>
        <div className="mt-1 flex items-start gap-2.5">
          <Checkbox id="su-terms" checked={agreed} onCheckedChange={(v) => setAgreed(Boolean(v))} className="mt-0.5" />
          <Label htmlFor="su-terms" className="text-[13px] font-normal leading-relaxed text-muted-foreground">
            I agree to the Xamseen{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="font-medium text-plum hover:underline">Terms of Service</a>{" "}
            and{" "}
            <a href="/community-guidelines" target="_blank" rel="noreferrer" className="font-medium text-plum hover:underline">Community Guidelines</a>,
            and I understand how Xamseen uses my data as described in the{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-plum hover:underline">Privacy Policy</a>.
          </Label>
        </div>
        <FieldError>{errors["agreed"]}</FieldError>
        <Button type="submit" disabled={busy} className="mt-1 h-11 w-full font-medium">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
    </AuthShell>
  );
}

/* --------------------------- Check your email --------------------------- */

export function CheckEmail({ onBack }: { onBack?: () => void }) {
  return (
    <AuthShell
      title="Verify your email address"
      subtitle="One quick security step: confirm this email address belongs to you."
    >
      <div className="rounded-xl border border-border bg-muted/40 p-4 text-[13px] leading-relaxed text-muted-foreground">
        <MailCheck className="mb-2 h-5 w-5 text-primary" />
        We've sent a verification link to your inbox. Open it to activate your account — your
        Xamseen welcome email arrives right after that. The link expires after a while; you can
        request a new one from the sign-in page at any time.
      </div>

      {onBack ? (
        <Button variant="outline" className="mt-6 h-11 w-full" onClick={onBack}>
          Back to sign in
        </Button>
      ) : (
        <Button asChild variant="outline" className="mt-6 h-11 w-full">
          <Link to="/signin">Back to sign in</Link>
        </Button>
      )}
    </AuthShell>
  );
}

/* --------------------------- Forgot password --------------------------- */

function ForgotPassword({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setError("Email is required.");
    if (!EMAIL_RE.test(email.trim())) return setError("Please enter a valid email address.");
    setError(undefined);
    setBusy(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (err) return setError(err.message);
    setSent(true);
  };

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="We've sent you a secure link to reset your password.">
        <Button variant="outline" className="h-11 w-full" onClick={onBack}>
          Back to sign in
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email address on your account and we'll send you a secure reset link."
      footer={<Link to="/" className="text-primary hover:underline">Back to Xamseen</Link>}
    >
      <form onSubmit={submit} noValidate className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="fp-email">Email</Label>
          <Input id="fp-email" type="email" className="h-11" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <FieldError>{error}</FieldError>
        </div>
        <Button type="submit" disabled={busy} className="h-11 w-full font-medium">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reset link
        </Button>
        <Button type="button" variant="ghost" className="h-11 w-full" onClick={onBack}>
          Back to sign in
        </Button>
      </form>
    </AuthShell>
  );
}
